import { apiService } from './index';
import { ISelect } from '../shared/types/shared';
import { extractOrbitApiErrorMessage } from '../shared/utils/orbitApiError';
import {
    CreateOrbitCasePayload,
    OrbitCaseCreateResponse,
    OrbitCatalogEnum,
    OrbitCatalogQuery,
} from '../shared/types/orbit';

type ApiItemsResponse<T> = {
    items?: T;
    statusCode?: number;
    message?: string | string[];
};

type OrbitCaseRecord = Record<string, unknown>;

const toCaseRecords = (value: unknown): OrbitCaseRecord[] => {
    if (!Array.isArray(value)) return [];

    return value.filter(
        (item): item is OrbitCaseRecord =>
            item !== null && typeof item === 'object'
    );
};

const isOrbitCaseRecord = (record: Record<string, unknown>): boolean =>
    record.id !== undefined &&
    record.id !== null &&
    String(record.id).trim() !== '';

const unwrapOrbitCase = (payload: unknown): OrbitCaseRecord | undefined => {
    if (!payload || typeof payload !== 'object') return undefined;

    const root = payload as OrbitCaseRecord;
    const items = root.items;

    if (Array.isArray(items) && items[0]) {
        return items[0];
    }

    if (items && typeof items === 'object' && !Array.isArray(items)) {
        const node = items as OrbitCaseRecord;
        if (isOrbitCaseRecord(node)) {
            return node;
        }
        const nested = node.data ?? node.items;
        if (
            nested &&
            typeof nested === 'object' &&
            !Array.isArray(nested) &&
            isOrbitCaseRecord(nested as OrbitCaseRecord)
        ) {
            return nested as OrbitCaseRecord;
        }
    }

    if (isOrbitCaseRecord(root)) {
        return root;
    }

    const data = root.data;
    if (
        data &&
        typeof data === 'object' &&
        !Array.isArray(data) &&
        isOrbitCaseRecord(data as OrbitCaseRecord)
    ) {
        return data as OrbitCaseRecord;
    }

    return undefined;
};

const extractCaseList = (
    payload: Record<string, unknown>
): OrbitCaseRecord[] => {
    const nested = payload.items ?? payload.data ?? payload.cases;

    if (Array.isArray(nested)) {
        return toCaseRecords(nested);
    }

    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
        const node = nested as Record<string, unknown>;

        if (isOrbitCaseRecord(node)) {
            return [node];
        }

        const inner = node.items ?? node.data ?? node.cases;
        if (Array.isArray(inner)) {
            return toCaseRecords(inner);
        }
    }

    if (isOrbitCaseRecord(payload)) {
        return [payload];
    }

    return [];
};

const unwrapOrbitCasesList = (
    payload: unknown
): { items: OrbitCaseRecord[]; total: number } => {
    if (!payload) return { items: [], total: 0 };

    if (Array.isArray(payload)) {
        const items = toCaseRecords(payload);
        return { items, total: items.length };
    }

    if (typeof payload !== 'object') {
        return { items: [], total: 0 };
    }

    const root = payload as Record<string, unknown>;
    const items = extractCaseList(root);
    const nested = root.items as Record<string, unknown> | undefined;
    const total = Number(
        root.total ?? nested?.total ?? root.total_count ?? items.length
    );

    return {
        items,
        total: Number.isFinite(total) ? total : items.length,
    };
};

type OrbitCatalogIdMode = 'uuid' | 'code' | 'value';

const getOrbitCatalogIdMode = (
    catalog: OrbitCatalogEnum
): OrbitCatalogIdMode => {
    switch (catalog) {
        case OrbitCatalogEnum.CaseTypes:
        case OrbitCatalogEnum.Countries:
        case OrbitCatalogEnum.Departments:
        case OrbitCatalogEnum.Cities:
            return 'code';
        case OrbitCatalogEnum.DocumentTypes:
        case OrbitCatalogEnum.PersonTypes:
        case OrbitCatalogEnum.Genders:
        case OrbitCatalogEnum.SpecialConditions:
            return 'value';
        default:
            return 'uuid';
    }
};

const resolveOrbitCatalogItemId = (
    record: Record<string, unknown>,
    mode: OrbitCatalogIdMode
): string => {
    switch (mode) {
        case 'code':
            return String(record.code ?? record.value ?? record.id ?? '');
        case 'value':
            return String(record.value ?? record.code ?? record.id ?? '');
        default:
            return String(record.id ?? record.code ?? record.value ?? '');
    }
};

const extractOrbitCatalogItems = (payload: unknown): unknown[] => {
    if (!payload) return [];

    if (Array.isArray(payload)) {
        return payload;
    }

    if (typeof payload !== 'object') return [];

    const record = payload as Record<string, unknown>;
    const nested = record.data ?? record.items;

    if (Array.isArray(nested)) {
        return nested;
    }

    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
        const node = nested as Record<string, unknown>;
        const inner = node.data ?? node.items;
        if (Array.isArray(inner)) {
            return inner;
        }
    }

    return [];
};

const mapOrbitCatalogToSelect = (
    payload: unknown,
    catalog: OrbitCatalogEnum
): ISelect[] => {
    const items = extractOrbitCatalogItems(payload);
    const idMode = getOrbitCatalogIdMode(catalog);

    return items.map((item) => {
        if (typeof item === 'string') {
            return { id: item, name: item, code: item };
        }

        const record = item as Record<string, unknown>;
        const id = resolveOrbitCatalogItemId(record, idMode);
        const name =
            record.label ?? record.name ?? record.description ?? String(id);
        const code = record.code
            ? String(record.code)
            : record.value
                ? String(record.value)
                : undefined;

        return {
            id,
            name: String(name),
            ...(code ? { code } : {}),
            ...(record.description
                ? { extra: String(record.description) }
                : {}),
        };
    });
};

const extractCreateCaseResponse = (
    response: ApiItemsResponse<OrbitCaseCreateResponse> | undefined
): OrbitCaseCreateResponse | undefined => {
    const payload = response?.items ?? response;

    if (!payload || typeof payload !== 'object') return undefined;

    return payload as OrbitCaseCreateResponse;
};

const CATALOG_CACHE_TTL_MS = 5 * 60 * 1000;

type CatalogCacheEntry = {
    items: ISelect[];
    error?: string;
    expiresAt: number;
};

const catalogCache = new Map<string, CatalogCacheEntry>();
const catalogInflight = new Map<
    string,
    Promise<{ items: ISelect[]; error?: string }>
>();

const buildCatalogCacheKey = (
    catalog: OrbitCatalogEnum,
    query?: OrbitCatalogQuery
): string => {
    const department = query?.department_code?.trim() || '';
    return `${catalog}|${department}`;
};

const formatApiError = (response: ApiItemsResponse<unknown>): string => {
    if (response.statusCode === 429) {
        return 'Demasiadas solicitudes a Orbit. Espere unos segundos e intente de nuevo.';
    }

    const orbitMessage = extractOrbitApiErrorMessage(
        response as Record<string, unknown>
    );

    if (orbitMessage) return orbitMessage;

    if (Array.isArray(response.message)) {
        return response.message.join('. ');
    }

    if (response.message) {
        return String(response.message);
    }

    return 'No se pudo cargar el catálogo de Orbit. Intente nuevamente.';
};

const fetchOrbitCatalogUncached = async (
    catalog: OrbitCatalogEnum,
    query?: OrbitCatalogQuery
): Promise<{ items: ISelect[]; error?: string }> => {
    try {
        const response = await apiService.get<ApiItemsResponse<unknown>>(
            `/api/integrations/orbit/catalogs/${catalog}`,
            query
        );

        if (response?.statusCode && response.statusCode >= 400) {
            return {
                items: [],
                error: formatApiError(response),
            };
        }

        const rawPayload = response?.items ?? response;
        const mapped = mapOrbitCatalogToSelect(rawPayload, catalog);

        if (mapped.length > 0) {
            return { items: mapped };
        }

        return {
            items: [],
            error: 'No se encontraron opciones en el catálogo de Orbit.',
        };
    } catch {
        return {
            items: [],
            error: 'No se pudo cargar el catálogo de Orbit. Intente nuevamente.',
        };
    }
};

export const asyncGetOrbitCatalog = async (
    catalog: OrbitCatalogEnum,
    query?: OrbitCatalogQuery
): Promise<{ items: ISelect[]; error?: string }> => {
    const cacheKey = buildCatalogCacheKey(catalog, query);
    const cached = catalogCache.get(cacheKey);

    if (cached && cached.expiresAt > Date.now()) {
        return { items: cached.items, error: cached.error };
    }

    const pending = catalogInflight.get(cacheKey);
    if (pending) {
        return pending;
    }

    const request = fetchOrbitCatalogUncached(catalog, query).then((result) => {
        catalogCache.set(cacheKey, {
            items: result.items,
            error: result.error,
            expiresAt: Date.now() + CATALOG_CACHE_TTL_MS,
        });
        catalogInflight.delete(cacheKey);
        return result;
    });

    catalogInflight.set(cacheKey, request);
    return request;
};

let citiesFlatPromise: Promise<{ items: ISelect[]; error?: string }> | null =
    null;

export const asyncGetOrbitCitiesFlat = async (): Promise<{
    items: ISelect[];
    error?: string;
}> => {
    if (!citiesFlatPromise) {
        citiesFlatPromise = (async () => {
            try {
                const response = await apiService.get<
                    ApiItemsResponse<ISelect[]>
                >('/api/integrations/orbit/catalogs/cities-flat');

                if (response?.statusCode && response.statusCode >= 400) {
                    return {
                        items: [],
                        error: formatApiError(response),
                    };
                }

                const items = Array.isArray(response?.items)
                    ? response.items
                    : [];

                if (!items.length) {
                    return {
                        items: [],
                        error: 'No se encontraron ciudades en Orbit.',
                    };
                }

                return { items };
            } catch {
                return {
                    items: [],
                    error: 'No se pudo cargar el catálogo de ciudades de Orbit.',
                };
            }
        })().then((result) => {
            if (result.error) {
                citiesFlatPromise = null;
            }
            return result;
        });
    }

    return citiesFlatPromise;
};

export const asyncGetOrbitCasesByDocument = async (
    document: string,
    page = 1,
    limit = 20
): Promise<{ items: Record<string, unknown>[]; length: number }> => {
    try {
        const response = await apiService.get<ApiItemsResponse<unknown>>(
            `/api/integrations/orbit/cases/by-document/${encodeURIComponent(
                document.trim()
            )}`,
            { page, limit }
        );

        if (response?.statusCode && response.statusCode >= 400) {
            return { items: [], length: 0 };
        }

        const { items, total } = unwrapOrbitCasesList(response?.items);

        return { items, length: total };
    } catch {
        return { items: [], length: 0 };
    }
};

export const asyncGetOrbitCaseById = async (
    caseId: string
): Promise<Record<string, unknown> | undefined> => {
    const trimmed = caseId.trim();
    if (!trimmed) return undefined;

    try {
        const response = await apiService.get<ApiItemsResponse<unknown>>(
            `/api/integrations/orbit/cases/${encodeURIComponent(trimmed)}`
        );

        if (response?.statusCode && response.statusCode >= 400) {
            return undefined;
        }

        return unwrapOrbitCase(response?.items);
    } catch {
        return undefined;
    }
};

export const asyncCreateOrbitCase = async (
    payload: CreateOrbitCasePayload
): Promise<OrbitCaseCreateResponse | undefined> => {
    const response = await apiService.post<
        ApiItemsResponse<OrbitCaseCreateResponse> | Record<string, unknown>
    >('/api/integrations/orbit/cases', payload);

    const errorPayload = response as Record<string, unknown>;
    const apiErrorMessage = extractOrbitApiErrorMessage(errorPayload);
    const statusCode = Number(errorPayload.statusCode);

    if ((Number.isFinite(statusCode) && statusCode >= 400) || apiErrorMessage) {
        throw response;
    }

    return extractCreateCaseResponse(
        response as ApiItemsResponse<OrbitCaseCreateResponse>
    );
};
